/* prettier-ignore */

import { Injectable, effect, inject, signal } from '@angular/core';
import { parseDates, areDatesValid } from './chat-actions/helpers/date.utils';
import { getCoordinates } from './chat-actions/helpers/google-location.utils';
import { GenerateRouteService } from './chat-actions/generate-route.service';
import { RegisterFormService } from './chat-actions/register-form.service';
import { ShowItemDetailsService } from './chat-actions/show-item-details.service';
import { ShowComponentService } from './chat-actions/show-component.service';
import { RemoveItemFromCustomRouteService } from './chat-actions/remove-item-from-custom-route.service';
import { AddItemToCustomRouteService } from './chat-actions/add-item-to-custom-route.service';
import { NewCustomRouteService } from 'src/app/pages/new-custom-route/new-custom-route.service';
import { isNotSuccess, validateRoute } from './chat-actions/guards/custom-route.guard';
import { GetFreeSlotsService } from './chat-actions/get-free-slots.service';
import { from, mapTo, Observable, of, take, tap } from 'rxjs';
import { DebugService } from 'src/app/services/debug.service';

@Injectable({ providedIn: 'root' })

export class ChatActionHandlerService {
    private _debugService                     = inject(DebugService);
    private _generateRouteService             = inject(GenerateRouteService);
    private _registerFormService              = inject(RegisterFormService);
    private _showItemDetails                  = inject(ShowItemDetailsService);
    private _showComponentService             = inject(ShowComponentService);
    private _removeItemFromCustomRouteService = inject(RemoveItemFromCustomRouteService);
    private _addItemToCustomRouteService      = inject(AddItemToCustomRouteService);
    private _customRouteService               = inject(NewCustomRouteService);
    private _getFreeSlotsService              = inject(GetFreeSlotsService)

    public  openAIFeedback = signal(null);
    private generatedRoute = signal(false);

    public actionFromChatBot(result): Observable<void> {
        this._debugService.info(result, '[CHAT ACTIONS HANDLER]');
        switch(result?.action){            
            case 'create_travel_dates':          return this.setTravelDates(result);         
            case 'create_travel_group_type':     return this.setTravelGroupType(result);     
            case 'create_travel_interests':      return this.setTravelInterests(result);   
            case 'create_starting_point':        return this.createStartingPoint(result);
            case 'get_current_itinerary':        return this.getCurrentRoute(result);
            case 'add_point_to_itinerary':       return this.addItem(result);                
            case 'remove_point_from_itinerary':  return this.removeItem(result);             
            case 'return_to_timeline':           return this.showTimeLineView(result);       
            case 'return_to_map':                return this.showMapView(result);            
            case 'show_route_day':               return this.showRouteDay(result);           
            case 'show_timeline_options':        return this.showTimeLineOptions(result);    
            case 'show_point_details':           return this.showItemDetails(result);        
            case 'go_to_mail_form':              return this.showSendRouteView(result);      
            default:                             return of(void 0);
        }
    }

    private generateInitialPayload(result: any) {
        return {
            action:         result.action,
            content:        null,
            is_successful:  false,
            operation_id:   result.operation_id,
            completion_id:  result.completion_id,
        }
    }

//---------- IA REGISTER USER TO GENERATE ROUTE ----------------------------------------
    private setTravelDates(item: any): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const dates = parseDates(item.start_date, item.end_date);
        if (!areDatesValid(dates)) {
            this.openAIFeedback.set({ 
                ...feedBack, 
                reason: 'invalid dates' 
            });
            return of(void 0);
        }

        this._registerFormService.setDateRange(item);
        if (this._registerFormService.isValidForm() && !this.generatedRoute()){
            const generatedRoute = this._generateRouteService.generateRoute();
            this.generatedRoute.set(true);
            this.openAIFeedback.set({ 
                ...feedBack, 
                is_successful: true, 
                content: generatedRoute 
            });
            return of(void 0);
        }
        this.openAIFeedback.set({ 
            ...feedBack, 
            is_successful: true 
        });
        return of(void 0);
    }

    private setTravelGroupType(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setTripType(item.group_id);
        if (this._registerFormService.isValidForm() && !this.generatedRoute()){
            const generatedRoute = this._generateRouteService.generateRoute();
            this.generatedRoute.set(true);
            this.openAIFeedback.set({ 
                ...feedBack, 
                ...res, 
                content: generatedRoute 
            });
            return of(void 0);
        }
        this.openAIFeedback.set({ 
            ...feedBack, 
            ...res 
        });
        return of(void 0);
    }

    private setTravelInterests(item):  Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setInterestIdList(item.interest_ids)
        if (this._registerFormService.isValidForm() && !this.generatedRoute()){
            const generatedRoute = this._generateRouteService.generateRoute();
            this.generatedRoute.set(true);
            this.openAIFeedback.set({ 
                ...feedBack, 
                ...res, 
                content: generatedRoute 
            });
            return of(void 0);
        }
        this.openAIFeedback.set({ 
            ...feedBack, 
            ...res 
        });
        return of(void 0);
    }

    private createStartingPoint(args: any): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        return from(getCoordinates(args?.starting_point)).pipe(
            tap(startLocation => {
                if (!startLocation) {
                    this.openAIFeedback.set({
                        ...feedBack,
                        is_successful: false,
                        reason: 'No se ha podido establecer el punto de partida'
                    });
                    return;
                }
                const address = { 
                    ...startLocation, 
                    title: args?.starting_point 
                };
                const res = this._registerFormService.setStartLocation(address);
                this.openAIFeedback.set({ 
                    ...feedBack, 
                    ...res 
                });
            }),
            mapTo(void 0),
            take(1)
        );
    }

//---------- IA CHATBOT CUSTOM ROUTE FUNCTIONS ----------------------------------------
    private showItemDetails(item: any): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        return this._showItemDetails.show(item).pipe(
            take(1), // garantiza completar
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private addItem(args: any): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        return this._addItemToCustomRouteService.addItem(args).pipe(
            take(1),
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private removeItem(args): Observable<void>{
        const feedBack = this.generateInitialPayload(args);
        return this._removeItemFromCustomRouteService.removeItem(args).pipe(
            take(1),
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private showTimeLineView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showTimelineComponent();
        this.openAIFeedback.set({...feedBack, ...res});
        return of(void 0);
    }

    private showMapView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showMapComponent();
        this.openAIFeedback.set({...feedBack, ...res});
        return of(void 0);
    }

    private showSendRouteView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showSendRouteComponent();
        this.openAIFeedback.set({...feedBack, ...res});
        return of(void 0);
    }

    private showTimeLineOptions(args):  Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        return this._getFreeSlotsService.getAvailableItemsOnSlot(args).pipe(
            take(1),
            tap((res: any) => {
                if (!res?.is_successful){
                    this.openAIFeedback.set({...feedBack, res});
                    return;
                }

                // let parsedResponse = JSON.parse(res?.content)
                this.openAIFeedback.set({ 
                    ...feedBack, 
                    content: res?.content,
                    'is_successful': true, 
                }) 
            }),
            mapTo(void 0)
        );
    }

    private showRouteDay(args): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        const route = this._customRouteService.route();

        const ctx = validateRoute(route, args?.day);
        if (isNotSuccess(ctx)){
            this.openAIFeedback.set({...feedBack, ctx});
            return of(void 0);    
        }

        const dayIndex = Number(args?.day - 1);
        this._customRouteService.setDay(dayIndex);
        this.openAIFeedback.set({...feedBack, is_successful: true});
        return of(void 0);
    }

    private getCurrentRoute(args): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        const route = this._customRouteService.route();

        const ctx = validateRoute(route, 1);
        if (isNotSuccess(ctx)){
            this.openAIFeedback.set({...feedBack, ctx});
            return of(void 0);    
        }

        const mappedRoute = this._generateRouteService.getCurrentRoute();
        this.openAIFeedback.set({...feedBack, is_successful: true, content: mappedRoute});
        return of(void 0);
    }

}